import { GetSearchResultsQueryParams } from '@app/data/clients/shared/types';
import { ParsedUrlQuery } from 'querystring';

/**
 * Known search query parameters that can appear in the URL query string.
 */
export const fixedSearchQueryParams = [
  'artistUserName',
  'cat_context',
  'collections',
  'featuredArtist',
  'iaCode',
  'includeSuggestion',
  'medium',
  'page',
  'pageSize',
  'queryParamItems',
  'rbs',
  'searchType',
  'showMatureContent',
  'sortOrder',
] as const;

export type FixedSearchQueryParam = (typeof fixedSearchQueryParams)[number];

/**
 * Search parameters extracted from the Next.js router query string.
 * This is a subset of `GetSearchResultsQueryParams` containing only the params
 * that come from the URL query string.
 *
 * Other params like `query` come from the route path;
 * `locale`, and `country` come from headers/cookies.
 * These are added later at the RbServerSideClient layer.
 */
export type SearchRouterQueryParams = Pick<
  GetSearchResultsQueryParams,
  FixedSearchQueryParam
>;

function isFixedSearchQueryParam(key: string): key is FixedSearchQueryParam {
  if (fixedSearchQueryParams.includes(key as FixedSearchQueryParam))
    return true;
  return false;
}

/**
 * Extracts search parameters from the Next.js router query object.
 *
 * This function parses `context.query` (from getServerSideProps) or `router.query`
 * (client-side) and extracts the known search params. Any unrecognized query params
 * are collected into the `queryParamItems` array for dynamic filter handling.
 *
 * Note that this function does NOT handle `query`, `locale`, or `country` params.
 * These are merged in later by `RbServerSideClient.GetSearchResults()`.
 *
 * see historical shop implementation https://github.com/redbubble/shop/blob/f88510f2d906f7789dc8e8d5f6a3aebde11299b5/app/shared/containers/withSearchResultQueryParams/index.js
 */
export const getSearchParams = (
  urlQuery: ParsedUrlQuery | Record<string, string[]>
): SearchRouterQueryParams => {
  const urlQueryKeys = Object.keys(urlQuery);
  const params: Partial<SearchRouterQueryParams> = urlQueryKeys.reduce(
    (acc, curr) => {
      if (!isFixedSearchQueryParam(curr)) return acc;

      switch (curr) {
        case 'page':
          acc[curr] = /^\+?\d+(\.\d*)?$/.test(String(urlQuery[curr]))
            ? parseInt(String(urlQuery[curr]), 10)
            : 1;
          break;
        case 'pageSize':
          acc[curr] = /^\+?\d+(\.\d*)?$/.test(String(urlQuery[curr]))
            ? parseInt(String(urlQuery.pageSize), 10)
            : null;
          break;
        default:
          acc[curr] = urlQuery[curr];
          break;
      }
      return acc;
    },
    {}
  );

  // Let "extraParams" be the request query parameters, restricted only to the non-fixed ones.
  const extraParams: SearchRouterQueryParams['queryParamItems'] = [];
  Object.entries(urlQuery).forEach(([key, value]) => {
    if (!isFixedSearchQueryParam(key) && value) {
      if (Array.isArray(value)) {
        extraParams.push({ name: key, values: value });
      } else {
        extraParams.push({ name: key, values: [value] });
      }
    }
  });

  // The non-fixed query parameters are relegated to the "queryParamItems" field.
  if (extraParams.length > 0) params.queryParamItems = extraParams;

  return params;
};
